/* eslint: semi: 1 */
import { computed } from 'vue';
import { useScheduleQueryStore } from '@/pinia/stores/schedule/scheduleQueryStore';
import { type ScheduleQuery } from '@/pinia/stores/schedule/scheduleListStore';
import Utils from '@/globals/utils';
import { DateTime } from 'luxon';

export type RouteQuery = {
  search?: string;
  tags?: string[];
  date?: string;
};

export function useScheduleQuery () {
  const queryStore = useScheduleQueryStore();

  const searchText = computed(() => queryStore.searchText);
  const isFiltering = computed(() => queryStore.hasActiveFilters);

  /**
   * Update the URL query params for the given key with the given values.
   * @param key The query param key to update.
   * @param query The query param values to set for the given key.
   */
  function updateURLQueryParams (key: string, query: string[]): void {
    Utils.updateWindowLocationQueryParams([
      { key, values: query }
    ]);
  }

  /**
   * Apply the given search text to the query store and update the URL
   * query params.
   * @param text The search text to apply.
   */
  function applySearchText (text: string): void {
    updateURLQueryParams('search', [text]);
    queryStore.setSearchText(text);
  }

  /**
   * Clear the search text from the query store and update the URL query
   * params.
   */
  function clearSearchText (): void {
    updateURLQueryParams('search', []);
    queryStore.setSearchText('');
  }

  /**
   * Apply the given date to the query store and update the URL query
   * params.
   * @param date The date to apply.
   */
  function applySelectedDate (date: string): void {
    updateURLQueryParams('date', date ? [date] : []);
    queryStore.setSelectedDate(date);
  }

  /**
   * Clear the selected date from the query store and update the URL
   * query params.
   */
  function clearSelectedDate (): void {
    updateURLQueryParams('date', []);
    queryStore.setSelectedDate(null);
  }

  /**
   * Apply the given tags to the query store and update the URL query
   * params.
   * @param tags The tags to apply.
   */
  function applySelectedTags (tags: string[]): void {
    updateURLQueryParams('tags', tags);
    queryStore.setSelectedTags(tags);
  }

  /**
   * Clear the selected tags from the query store and update the URL
   * query params.
   */
  function clearSelectedTags (): void {
    updateURLQueryParams('tags', []);
    queryStore.setSelectedTags([]);
  }

  function resetQuery (): void {
    queryStore.$reset();
  }

  /**
   * Sets the query params for the initial page load based on
   * the current route query (e.g. search, tags).
   */
  function setScheduleQueryParams (query: RouteQuery) {
    if (query.search) {
      queryStore.setSearchText(query.search as string);
    }

    if (query.tags) {
      queryStore.setSelectedTags(query.tags as string[]);
    }

    if (query.date) {
      queryStore.setSelectedDate(query.date as string);
    }
  }

  /**
   * Build the query params for the schedule list API based on the current
   * state of the schedule query store (e.g. search, tags, start, max).
   */
  function getQueryParams (): ScheduleQuery {
    const queryParams: ScheduleQuery = { max: queryStore.max, width: 512 };

    if (queryStore.searchText) {
      queryParams.search = queryStore.searchText;
    }
    if (queryStore.selectedDate) {
      // UTC date string
      const utcDate = DateTime.fromISO(queryStore.selectedDate, { zone: 'utc' });

      if (utcDate.isValid) {
        queryParams.start = utcDate.toISO();
      }
    }
    if (queryStore.selectedTags.length > 0) {
      queryParams.tags = JSON.stringify(queryStore.selectedTags);
    }

    return queryParams;
  }

  return {
    // computed properties
    searchText,
    isFiltering,

    // Setter functions for the query store state
    applySearchText,
    applySelectedDate,
    applySelectedTags,
    resetQuery,
    clearSearchText,
    clearSelectedTags,
    clearSelectedDate,

    // Functions setting and getting query params for the schedule list API
    setScheduleQueryParams,
    getQueryParams,
  };
}
